import React, { useState, useCallback, useEffect } from 'react';
import { X, Save } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  addDoc,
  collection,
  updateDoc,
  doc
} from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import toast from 'react-hot-toast';

const AddDiscountForm = ({ onClose, onSuccess, editingDiscount = null }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    discountType: 'percentage',
    discountValue: 0,
    minOrderAmount: 0,
    maxDiscountAmount: 0,
    validFrom: '',
    validUntil: '',
    usageLimit: 0,
    isActive: true,
    applicableCategories: [],
    applicableItems: []
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  }, []);

  const handleNumberChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: parseFloat(value) || 0
    }));
  }, []);

  const handleUsageLimitChange = useCallback((e) => {
    const { value } = e.target;
    setFormData(prev => ({
      ...prev,
      usageLimit: parseInt(value) || 0
    }));
  }, []);

  useEffect(() => {
    if (editingDiscount) {
      setFormData({
        name: editingDiscount.name || '',
        description: editingDiscount.description || '',
        discountType: editingDiscount.discountType || 'percentage',
        discountValue: editingDiscount.discountValue || 0,
        minOrderAmount: editingDiscount.minOrderAmount || 0,
        maxDiscountAmount: editingDiscount.maxDiscountAmount || 0,
        validFrom: editingDiscount.validFrom || '',
        validUntil: editingDiscount.validUntil || '',
        usageLimit: editingDiscount.usageLimit || 0,
        isActive: editingDiscount.isActive !== false,
        applicableCategories: editingDiscount.applicableCategories || [],
        applicableItems: editingDiscount.applicableItems || []
      });
    }
  }, [editingDiscount]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      console.log('🎯 Form submission started...');
      console.log('Form data:', formData);

      // Validate required fields
      if (!formData.name || !formData.description) {
        toast.error('Please fill in all required fields');
        setIsLoading(false);
        return;
      }

      const discountData = {
        ...formData,
        createdAt: editingDiscount ? editingDiscount.createdAt : new Date(),
        updatedAt: new Date()
      };

      console.log('Discount data to save:', discountData);
      
      if (editingDiscount) {
        console.log('Updating existing discount:', editingDiscount.id);
        await updateDoc(doc(db, 'discounts', editingDiscount.id), discountData);
        toast.success('Discount updated successfully!');
      } else {
        console.log('Adding new discount to Firestore...');
        const docRef = await addDoc(collection(db, 'discounts'), discountData);
        console.log('New discount added with ID:', docRef.id);
        toast.success('Discount added successfully!');
      }

      console.log('🔄 Calling onSuccess callback...');
      onSuccess();
      console.log('🚪 Calling onClose callback...');
      onClose();
    } catch (error) {
      console.error('❌ Error saving discount:', error);
      toast.error(`Failed to save discount: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      zIndex: 1000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '20px',
        padding: '32px',
        width: '100%',
        maxWidth: '600px',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px'
        }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: '700',
            color: '#1f2937',
            margin: 0
          }}>
            {editingDiscount ? 'Edit Discount' : 'Add New Discount'}
          </h2>
          <button onClick={onClose} style={{
            padding: '8px',
            backgroundColor: '#f3f4f6',
            color: '#6b7280',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <X style={{ width: '20px', height: '20px' }} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <Label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px', display: 'block' }}>
                Discount Name *
              </Label>
              <Input
                type="text"
                name="name"
                placeholder="Enter discount name"
                value={formData.name}
                onChange={handleInputChange}
                required
                style={{
                  padding: '12px',
                  borderRadius: '8px',
                  border: '2px solid #e5e7eb',
                  fontSize: '14px',
                  width: '100%',
                  outline: 'none',
                  transition: 'border-color 0.2s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = '#ff6b35'}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
              />
            </div>
            <div>
              <Label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px', display: 'block' }}>
                Discount Type *
              </Label>
              <select
                name="discountType"
                value={formData.discountType}
                onChange={handleInputChange}
                required
                style={{
                  padding: '12px',
                  borderRadius: '8px',
                  border: '2px solid #e5e7eb',
                  fontSize: '14px',
                  width: '100%',
                  outline: 'none',
                  backgroundColor: 'white',
                  transition: 'border-color 0.2s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = '#ff6b35'}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
              >
                <option value="percentage">Percentage</option>
                <option value="fixed">Fixed Amount</option>
              </select>
            </div>
          </div>

          <div>
            <Label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px', display: 'block' }}>
              Description *
            </Label>
            <textarea
              name="description"
              placeholder="Enter discount description"
              value={formData.description}
              onChange={handleInputChange}
              required
              rows={3}
              style={{
                padding: '12px',
                borderRadius: '8px',
                border: '2px solid #e5e7eb',
                fontSize: '14px',
                width: '100%',
                outline: 'none',
                resize: 'vertical',
                transition: 'border-color 0.2s ease'
              }}
              onFocus={(e) => e.target.style.borderColor = '#ff6b35'}
              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <Label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px', display: 'block' }}>
                {formData.discountType === 'percentage' ? 'Discount Percentage (%)' : 'Discount Amount (₹)'} *
              </Label>
              <Input
                type="number"
                name="discountValue"
                placeholder={formData.discountType === 'percentage' ? 'Enter percentage' : 'Enter amount'}
                value={formData.discountValue}
                onChange={handleNumberChange}
                required
                min="0"
                max={formData.discountType === 'percentage' ? '100' : undefined}
                step="0.01"
                style={{
                  padding: '12px',
                  borderRadius: '8px',
                  border: '2px solid #e5e7eb',
                  fontSize: '14px',
                  width: '100%',
                  outline: 'none',
                  transition: 'border-color 0.2s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = '#ff6b35'}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
              />
            </div>
            <div>
              <Label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px', display: 'block' }}>
                Minimum Order Amount (₹) *
              </Label>
              <Input
                type="number"
                name="minOrderAmount"
                placeholder="Enter minimum order amount"
                value={formData.minOrderAmount}
                onChange={handleNumberChange}
                required
                min="0"
                step="0.01"
                style={{
                  padding: '12px',
                  borderRadius: '8px',
                  border: '2px solid #e5e7eb',
                  fontSize: '14px',
                  width: '100%',
                  outline: 'none',
                  transition: 'border-color 0.2s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = '#ff6b35'}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <Label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px', display: 'block' }}>
                Valid From *
              </Label>
              <Input
                type="datetime-local"
                name="validFrom"
                value={formData.validFrom}
                onChange={handleInputChange}
                required
                style={{
                  padding: '12px',
                  borderRadius: '8px',
                  border: '2px solid #e5e7eb',
                  fontSize: '14px',
                  width: '100%',
                  outline: 'none',
                  transition: 'border-color 0.2s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = '#ff6b35'}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
              />
            </div>
            <div>
              <Label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px', display: 'block' }}>
                Valid Until *
              </Label>
              <Input
                type="datetime-local"
                name="validUntil"
                value={formData.validUntil}
                onChange={handleInputChange}
                required
                style={{
                  padding: '12px',
                  borderRadius: '8px',
                  border: '2px solid #e5e7eb',
                  fontSize: '14px',
                  width: '100%',
                  outline: 'none',
                  transition: 'border-color 0.2s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = '#ff6b35'}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div>
              <Label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px', display: 'block' }}>
                Usage Limit (0 = Unlimited)
              </Label>
              <Input
                type="number"
                placeholder="Enter usage limit"
                value={formData.usageLimit}
                onChange={handleUsageLimitChange}
                min="0"
                style={{
                  padding: '12px',
                  borderRadius: '8px',
                  border: '2px solid #e5e7eb',
                  fontSize: '14px',
                  width: '100%',
                  outline: 'none',
                  transition: 'border-color 0.2s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = '#ff6b35'}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
              />
            </div>
            <div>
              <Label style={{ fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px', display: 'block' }}>
                Max Discount Amount (₹)
              </Label>
              <Input
                type="number"
                name="maxDiscountAmount"
                placeholder="Enter max discount amount"
                value={formData.maxDiscountAmount}
                onChange={handleNumberChange}
                min="0"
                step="0.01"
                style={{
                  padding: '12px',
                  borderRadius: '8px',
                  border: '2px solid #e5e7eb',
                  fontSize: '14px',
                  width: '100%',
                  outline: 'none',
                  transition: 'border-color 0.2s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = '#ff6b35'}
                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
              />
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                name="isActive"
                checked={formData.isActive}
                onChange={handleInputChange}
                style={{ width: '16px', height: '16px' }}
              />
              <span style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>
                Active Discount
              </span>
            </label>
          </div>

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <Button
              type="button"
              onClick={onClose}
              style={{
                padding: '12px 24px',
                backgroundColor: '#f3f4f6',
                color: '#6b7280',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              style={{
                padding: '12px 24px',
                backgroundColor: '#ff6b35',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                opacity: isLoading ? 0.7 : 1
              }}
            >
              {isLoading ? (
                <div className="spinner" style={{ width: '16px', height: '16px', border: '2px solid white', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
              ) : (
                <Save style={{ width: '16px', height: '16px' }} />
              )}
              {editingDiscount ? 'Update Discount' : 'Add Discount'}
            </Button>
          </div>
        </form>
      </div>
      <style>{`@keyframes spin {0% { transform: rotate(0deg); }100% { transform: rotate(360deg); }}`}</style>
    </div>
  );
};

export default AddDiscountForm;





